/* eslint-disable max-lines-per-function */
import React, { useContext, useReducer } from "react";
import { Grid, Button, Card, CardContent } from "@mui/material";
import {
  ICarrier,
  TContractOwner,
  ICredentialsStep,
} from "../../interfaces/configuration";
import { UserContext } from "../../contexts/userContext";
import {
  ECOMMERCE,
  ERROR,
  MARKETPLACE,
  OWN_FLEET,
} from "../../utils/constants";
import { IAlert } from "../../interfaces/alert";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { carriersReducer } from "../../reducers/carriersReducer";
import { isCaseInsensitiveEqual } from "../../utils/text";
import { TEntity } from "../../interfaces/userContext";
import AutoCompleteStrategyFactory from "../../strategies/autocomplete/AutocompleteStrategy";
import {
  integrateCarriersWithCredentials,
  integrateDefaultCarrier,
} from "../../services/carrierIntegrationService";
import CarrierCredentialsStrategy from "../../strategies/carrierCredentials/CarrierCredentialsStrategy";
import SkipStepButton from "./SkipStepButton";
import CarrierSelectionForm from "./CarrierSelectionForm";

interface CarriersProps {
  handleBack: () => void;
  handleSkip?: () => void;
  isOptional?: boolean;
  showOwnFleetInfo?: boolean;
  infoMessage?: string;
  isLastStep: boolean;
}

const carrierStates = {
  selectedCarriers: [],
  showCredentialForm: false,
  alert: {} as IAlert,
  loading: false,
};

const getFilteredCarriers = (
  carriers: ICarrier[],
  entity: TEntity,
  contractOwner?: TContractOwner,
) => {
  const filteredCarriers = carriers.filter(({ name }) => {
    const isOwnFleet = isCaseInsensitiveEqual(name, OWN_FLEET);
    const shouldFilter =
      isOwnFleet &&
      (entity === ECOMMERCE ||
        (entity === MARKETPLACE && contractOwner === MARKETPLACE));
    return !shouldFilter;
  });
  return filteredCarriers;
};

const CarriersInitialConfig = ({
  handleBack,
  handleSkip,
  isOptional,
  showOwnFleetInfo,
  infoMessage,
  isLastStep,
}: CarriersProps) => {
  const {
    currentUser: { entity },
    carriers,
    rules: { contractOwner },
  } = useContext(UserContext);

  const [states, dispatch] = useReducer(carriersReducer, carrierStates);
  const { selectedCarriers, showCredentialForm, alert, loading } = states;
  const navigate = useNavigate();

  // va a depender del tipo de contrato de un mkt o si la entidad es ecommerce.
  const singleCarrierMode =
    contractOwner === MARKETPLACE || entity === ECOMMERCE;
  const initialConfigSuccessPath = `/configuracion-inicial-${
    entity === MARKETPLACE ? "tienda" : "ecommerce"
  }-exitosa`;
  const carriersFilteredAndParsed = getFilteredCarriers(
    carriers,
    entity,
    contractOwner,
  );

  const changeSelectedCarriersValue = (value: any, reason: string) => {
    if (!value || reason === "clear") {
      dispatch({ type: "setSelectedCarriers", value: [] });
      return;
    }
    dispatch({ type: "setSelectedCarriers", value });
  };

  // In the short term we'll handle both cases (with and without carriers credentials)
  // it'll be replaced when we adapt the register client process into the backend.
  const handleFinishConfiguration = async (withCarriers: boolean, carriersToUpdate?: ICarrier[]) => {
    dispatch({ type: "setLoading", value: true });

    try {
      if (withCarriers && carriersToUpdate && carriersToUpdate.length > 0) {
        const result = await integrateCarriersWithCredentials(
          carriersToUpdate,
          entity,
        );

        if (!result.success) {
          throw new Error(result.error);
        }
      } else {
        const defaultCarrier = carriersFilteredAndParsed[0];

        if (!defaultCarrier) {
          throw new Error("No hay operadores logísticos disponibles");
        }

        const result = await integrateDefaultCarrier(defaultCarrier, entity);

        if (!result.success) {
          throw new Error(result.error);
        }

        handleSkip?.();
      }

      navigate(initialConfigSuccessPath);
    } catch (error: any) {
      dispatch({
        type: "setAlert",
        value: {
          typeOfAlert: ERROR,
          message: error.message || error,
          showAlert: true,
        },
      });
    } finally {
      dispatch({ type: "setLoading", value: false });
    }
  };

  const handleCancel = () =>
    dispatch({ type: "setShowCredentialForm", value: false });

  const handleSelectCarriers = () =>
    singleCarrierMode
      ? dispatch({ type: "setShowCredentialForm", value: true })
      : handleFinishConfiguration(true);

  const handleSkipStep = () => {
    if (isLastStep) {
      handleFinishConfiguration(false);
    } else {
      handleSkip?.();
    }
  };

  const autoCompleteStrategy =
    AutoCompleteStrategyFactory.getStrategy(singleCarrierMode);

  const handleAutoCompleteChange = (
    _: any,
    value: ICarrier | ICarrier[] | null,
    reason: string,
    details?: { option: ICarrier },
  ) => {
    const normalizedValue = autoCompleteStrategy.normalizeValue(value);

    const processedCarriers = autoCompleteStrategy.getAddedCarrier(
      normalizedValue,
      details?.option,
    );
    changeSelectedCarriersValue(processedCarriers, reason);
  };

  const getCarrierCredentialsForm = () => {
    const carrier = selectedCarriers[0];
    const configuration: ICredentialsStep = {
      handleSubmitCarriers: (carriersToUpdate: ICarrier[]) => handleFinishConfiguration(carriersToUpdate.length > 0, carriersToUpdate),
      alert,
      carrier,
      loading,
      handleCancel,
    };
    return CarrierCredentialsStrategy.getCredentialComponent(
      carrier.name,
      configuration,
    );
  };
  return (
    <Grid item xs={12} lg={12} xl={singleCarrierMode ? 10 : 7}>
      <Card className="initial-config-step-card bg-input br-10">
        <CardContent>
          {showCredentialForm ? (
            getCarrierCredentialsForm()
          ) : (
            <>
              <CarrierSelectionForm
                singleCarrierMode={singleCarrierMode}
                showOwnFleetInfo={showOwnFleetInfo}
                infoMessage={infoMessage}
                alert={alert}
                carriersFilteredAndParsed={carriersFilteredAndParsed}
                selectedCarriers={selectedCarriers}
                carriersLength={carriersFilteredAndParsed.length}
                onAutoCompleteChange={handleAutoCompleteChange}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button variant="outlined" onClick={handleBack} fullWidth>
                    Volver
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    onClick={handleSelectCarriers}
                    disabled={!selectedCarriers.length || loading}
                    loading={loading}
                    loadingPosition="end"
                  >
                    {singleCarrierMode ? "Continuar" : "Finalizar"}
                  </LoadingButton>
                </Grid>
                {isOptional && handleSkip && (
                  <SkipStepButton
                    onSkip={handleSkipStep}
                    helperText="Podrás agregar operadores logísticos desde Configuración"
                  />
                )}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default CarriersInitialConfig;
